/**
 * EntityProfileBlock - Display entity details and relationships
 */

import React from 'react'
import { EntityProfileBlock as EntityProfileBlockType, MetricValue } from '../../../../lib/cockpit/schemas'

interface EntityProfileBlockProps {
  data: EntityProfileBlockType['data']
  blockId: string
  compact?: boolean
}

function formatValue(value: MetricValue): string {
  if (typeof value === 'number') return value.toLocaleString()
  if (typeof value === 'string') return value
  if (typeof value === 'object' && value !== null && 'formatted' in value) return value.formatted || String(value.value)
  return String(value)
}

function EntityProfileBlock({ data, blockId, compact }: EntityProfileBlockProps) {
  if (compact) {
    return (
      <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <span className="text-blue-600 dark:text-blue-400 font-semibold text-lg">
            {data.name.charAt(0)}
          </span>
        </div>
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{data.name}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{data.type}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
          <span className="text-blue-600 dark:text-blue-400 font-bold text-2xl">
            {data.name.charAt(0)}
          </span>
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{data.name}</h3>
          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
            {data.type}
          </span>
          {data.description && (
            <p className="mt-2 text-gray-600 dark:text-gray-400 text-sm">
              {data.description}
            </p>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      {data.key_metrics && data.key_metrics.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Key Metrics</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {data.key_metrics.map((metric: any, i: number) => (
              <div key={i} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="text-xs text-gray-500 dark:text-gray-400">{metric.label}</div>
                <div className="mt-1 font-semibold text-gray-900 dark:text-white">
                  {formatValue(metric.value)}
                  {metric.unit && <span className="text-sm text-gray-500 ml-1">{metric.unit}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Relationships */}
      {data.relationships && data.relationships.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Relationships</h4>
          <div className="space-y-2">
            {data.relationships.map((rel: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs">
                  {rel.type}
                </span>
                <span className="text-gray-600 dark:text-gray-400">→</span>
                <span className="text-gray-900 dark:text-white font-medium">
                  {rel.target_entity_name}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mentioned in sections */}
      {data.mentioned_in_sections && data.mentioned_in_sections.length > 0 && (
        <div className="text-xs text-gray-500 dark:text-gray-500">
          Mentioned in: {data.mentioned_in_sections.join(', ')}
        </div>
      )}
    </div>
  )
}

export default EntityProfileBlock



